import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import type { OneCStockItem } from '../../types/onec-stock.types';

/** Агрегированная позиция остатков склада (по коду номенклатуры). */
export interface StockOption {
  code: string;
  name: string;
  available: number;
}

/**
 * Схлопывает остатки 1С в уникальные позиции: суммирует количество по всем
 * партиям одного медикамента, отбрасывает нулевые, сортирует по названию.
 */
export function aggregateStockOptions(
  items: OneCStockItem[] | undefined,
): StockOption[] {
  const map = new Map<string, StockOption>();
  for (const it of items ?? []) {
    const key = it.medicationCode || it.medication;
    if (!key) continue;
    const existing = map.get(key);
    if (existing) {
      existing.available += it.quantity;
    } else {
      map.set(key, {
        code: it.medicationCode,
        name: it.medication,
        available: it.quantity,
      });
    }
  }
  return [...map.values()]
    .filter((o) => o.available > 0)
    .sort((a, b) => a.name.localeCompare(b.name, 'ru'));
}

/**
 * Поисковый пикер медикаментов из остатков склада (замена нативного datalist,
 * который ненадёжен с кириллицей и длинными списками). Выбор только из списка.
 */
export function MedicationPicker({
  options,
  selectedName,
  onSelect,
}: {
  options: StockOption[];
  selectedName: string;
  onSelect: (opt: StockOption) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef<HTMLDivElement>(null);
  const [rect, setRect] = useState<{
    left: number;
    top: number;
    width: number;
  } | null>(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const base = q
      ? options.filter(
          (o) =>
            o.name.toLowerCase().includes(q) ||
            o.code.toLowerCase().includes(q),
        )
      : options;
    return base.slice(0, 50);
  }, [options, query]);

  // Позиция выпадающего списка — в координатах viewport (portal + position:fixed),
  // чтобы список не обрезался overflow-скроллом модалки/таблицы.
  const updateRect = () => {
    const el = wrapRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setRect({ left: r.left, top: r.bottom, width: r.width });
  };

  useLayoutEffect(() => {
    if (open) updateRect();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = () => updateRect();
    window.addEventListener('scroll', handler, true);
    window.addEventListener('resize', handler);
    return () => {
      window.removeEventListener('scroll', handler, true);
      window.removeEventListener('resize', handler);
    };
  }, [open]);

  // В инпуте: при открытом поиске — введённый текст, иначе — выбранное наименование.
  const inputValue = open ? query : selectedName;

  return (
    <div className="relative" ref={wrapRef}>
      <Input
        value={inputValue}
        placeholder="Начните вводить наименование…"
        onFocus={() => {
          setQuery('');
          setOpen(true);
        }}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onBlur={() => window.setTimeout(() => setOpen(false), 120)}
      />
      {open &&
        rect &&
        createPortal(
          <div
            className="fixed z-[60] max-h-80 overflow-y-auto rounded-md border bg-popover text-popover-foreground shadow-md"
            style={{
              left: rect.left,
              top: rect.top + 4,
              width: rect.width,
            }}
          >
            {filtered.length === 0 ? (
              <div className="px-3 py-2 text-sm text-muted-foreground">
                {options.length === 0
                  ? 'Нет доступных позиций'
                  : 'Ничего не найдено'}
              </div>
            ) : (
              filtered.map((o) => (
                <button
                  type="button"
                  key={o.code || o.name}
                  className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-accent hover:text-accent-foreground"
                  onMouseDown={(e) => {
                    // до onBlur — иначе список закроется раньше выбора
                    e.preventDefault();
                    onSelect(o);
                    setOpen(false);
                  }}
                >
                  <span className="truncate">
                    {o.name}
                    {o.code ? (
                      <span className="ml-1 font-mono text-xs text-muted-foreground">
                        {o.code}
                      </span>
                    ) : null}
                  </span>
                  <Badge variant="secondary" className="shrink-0">
                    {o.available}
                  </Badge>
                </button>
              ))
            )}
          </div>,
          document.body,
        )}
    </div>
  );
}
